/**
 * Creator-acquisition shared constants — objection taxonomy, scraped-link
 * sentinels, league derivation, and the funnel-stage classifier. Shared by the
 * CreatorContact schema, the scheduled-ops engine, and the admin stage board so
 * none of them drift.
 */
import { normalizeTag } from "../tagNormalization.js";
export const OBJECTION_REASONS = [
    "interested_later",
    "using_competitor",
    "not_interested",
    "no_bandwidth",
    "wrong_person",
    "unresponsive",
];
/** Calendar.link values marking a Stanza-generated (not owner-maintained) calendar. */
export const SCRAPED_CALENDAR_LINKS = [
    "scraped",
    "scraped-legacy-manual",
];
/** Sports-league Calendar.tags used to derive a creator's league. Values must
 *  match the tags stored in prod (e.g. `laliga`, not `la-liga`). */
export const LEAGUE_TAGS = [
    // g-league before nba: G-League teams carry both tags, so it must win.
    "nfl", "g-league", "nba", "nhl", "mlb", "mls", "wnba", "milb", "ahl", "echl", "ncaa",
    "premier-league", "laliga", "serie-a", "bundesliga", "ligue-1", "scottish-premiership",
];
/** Generic tags that never identify a league. */
const NON_LEAGUE_TAGS = ["soccer", "sports", "stanza-plus", "womens-sports"];
const NORMALIZED_LEAGUE_TAGS = LEAGUE_TAGS.map((l) => [normalizeTag(l), l]);
const NORMALIZED_NON_LEAGUE = new Set(NON_LEAGUE_TAGS.map(normalizeTag));
/** Funnel positions on the creator stage board, in funnel order. */
export const CREATOR_STAGES = ["COLD", "ENGAGED", "STARTED_CLAIM", "CONVERTED"];
export const BOUNCE_CLASSES = ["recipient_dead", "blocked", "invalid", "spam", "not_delivered"];
/** Days a click/reply counts as engagement for staging. */
export const ENGAGEMENT_WINDOW_DAYS = 30;
/** Touches with no engagement before the why-not ask + contact rotation. */
export const MAX_TOUCHES_BEFORE_ROTATE = 6;
/** Cap on per-contact outbound touch log entries. */
export const MAX_TOUCH_LOG_ENTRIES = 12;
/** Outbound channels a touch can be sent through. */
export const TOUCH_CHANNELS = ["sendgrid", "gmail"];
/**
 * Derive a creator's league from Calendar.tags — first matching sports-league
 * tag, else the first non-generic tag, else "other". Tags are normalized with
 * the shared `normalizeTag` (same as Ghost / sports-marketing) so display-form
 * and slug tags match; stored tag values are kept canonical in the DB.
 */
export function primaryLeagueFromTags(tags) {
    const norm = (tags ?? []).map(normalizeTag);
    for (const [normLeague, league] of NORMALIZED_LEAGUE_TAGS) {
        if (norm.includes(normLeague))
            return league;
    }
    const idx = norm.findIndex((n) => n.length > 0 && !NORMALIZED_NON_LEAGUE.has(n));
    return idx >= 0 ? norm[idx] : "other";
}
/**
 * Classify a creator handle into exactly one funnel stage.
 *
 * Precedence: a verified TrackedUrl (or recorded conversion) is CONVERTED via
 * the button; a non-scraped calendar is CONVERTED via their own feed; an open
 * claim-wizard entry is STARTED_CLAIM; recent click/reply engagement is
 * ENGAGED; otherwise COLD.
 *
 * @param input - Calendar, TrackedUrl, contact, and engagement facts.
 * @returns The stage plus, when CONVERTED, how they maintain the calendar.
 */
export function computeCreatorStage(input) {
    if (input.hasVerifiedTrackedUrl || input.convertedAt) {
        return { stage: "CONVERTED", conversionType: "verified-button" };
    }
    if (!input.isScraped) {
        return { stage: "CONVERTED", conversionType: "own-feed" };
    }
    if (input.startedClaim)
        return { stage: "STARTED_CLAIM" };
    if (input.engaged)
        return { stage: "ENGAGED" };
    return { stage: "COLD" };
}
/**
 * Trailing window for the board's ENGAGED gate (site gestures + replies).
 * Held at 90 days while the pipeline re-engages; tighten to
 * `ENGAGEMENT_WINDOW_DAYS` once outreach cadence resumes.
 */
export const BOARD_ENGAGED_WINDOW_DAYS = 90;
/** True when the date falls inside the trailing window of `windowDays` ending at `now`. */
export function withinEngagementWindow(date, now = Date.now(), windowDays = ENGAGEMENT_WINDOW_DAYS) {
    if (!date)
        return false;
    return new Date(date).getTime() >= now - windowDays * 86_400_000;
}
/**
 * Partial-schedule calendar names ("Home Games", "Away Games", and womens /
 * reserves variants). These are not a claimable team feed, so they're excluded
 * from the acquisition audience and the stage board — never named, targeted, or
 * cited as a peer.
 */
export const PARTIAL_SCHEDULE_NAME_RE = /\b(?:home|away)\s+games\s*$/i;
/**
 * Aggregation stages collecting per-handle calendar facts (summed subscribers,
 * display name, tags, open claim stamp, distinct links). Name and tags are
 * taken from the handle's highest-subscriber calendar (the primary team feed),
 * so a tiny "Home Games" / "Away Games" partial never names the handle. One
 * shape for the acquisition audience and the stage board so subscriber math
 * never diverges between them.
 *
 * @returns Pipeline stages to spread after the caller's `$match`.
 */
export function groupCalendarFactsByHandleStage() {
    return [
        {
            $set: {
                _subCount: {
                    $cond: [
                        { $isArray: "$subscribers" },
                        { $size: "$subscribers" },
                        { $ifNull: ["$subscribers", 0] },
                    ],
                },
            },
        },
        {
            $group: {
                _id: "$handle",
                subscribers: { $sum: "$_subCount" },
                name: { $top: { sortBy: { _subCount: -1 }, output: "$name" } },
                tags: { $top: { sortBy: { _subCount: -1 }, output: "$tags" } },
                startedClaim: { $max: "$startedClaim" },
                links: { $addToSet: "$link" },
            },
        },
    ];
}
//# sourceMappingURL=creatorAcquisition.js.map